import { X } from "lucide-react";
import { Dialog as D } from "radix-ui";
import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { DialogOverlay } from "./dialog";

export const Sheet = D.Root;
export const SheetTrigger = D.Trigger;
export const SheetClose = D.Close;

const WIDTHS = { sm: "sm:max-w-[400px]", md: "sm:max-w-[480px]", lg: "sm:max-w-[720px]", xl: "sm:max-w-[960px]" } as const;

export interface SheetContentProps extends ComponentPropsWithoutRef<typeof D.Content> {
  side?: "right" | "left";
  size?: keyof typeof WIDTHS;
  hideClose?: boolean;
}

/**
 * Side drawer. Right side (default) = record quick-view / create & edit forms;
 * left side = mobile navigation. Keeps the list behind it in context.
 */
export const SheetContent = forwardRef<ComponentRef<typeof D.Content>, SheetContentProps>(function SheetContent(
  { className, children, side = "right", size = "md", hideClose, ...props },
  ref,
) {
  return (
    <D.Portal>
      <DialogOverlay />
      <D.Content
        ref={ref}
        className={cn(
          "fixed inset-y-0 z-50 flex w-full flex-col bg-surface text-fg shadow-lg outline-none",
          side === "right"
            ? "right-0 border-l border-line data-[state=open]:animate-sheet-in-right data-[state=closed]:animate-sheet-out-right sm:rounded-l-panel"
            : "left-0 border-r border-line data-[state=open]:animate-sheet-in-left data-[state=closed]:animate-sheet-out-left",
          side === "right" ? WIDTHS[size] : "max-w-[300px]",
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <D.Close className="absolute right-3.5 top-3.5 inline-flex size-8 items-center justify-center rounded-control text-fg-muted transition-colors hover:bg-fg/[0.06] hover:text-fg" aria-label="Close">
            <X className="size-[18px]" />
          </D.Close>
        )}
      </D.Content>
    </D.Portal>
  );
});

export function SheetHeader({ title, description, eyebrow, actions, className }: { title: ReactNode; description?: ReactNode; eyebrow?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn("border-b border-line px-6 pb-4 pt-5 pr-14", className)}>
      {eyebrow && <div className="kicker mb-1.5 text-primary-ink">{eyebrow}</div>}
      <D.Title className="font-display text-xl font-semibold tracking-tight">{title}</D.Title>
      {description ? <D.Description className="mt-1 text-base text-fg-muted">{description}</D.Description> : <D.Description className="sr-only">{typeof title === "string" ? title : "Panel"}</D.Description>}
      {actions && <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function SheetBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-5", className)}>{children}</div>;
}

export function SheetFooter({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("flex items-center justify-end gap-2 border-t border-line bg-surface-2/40 px-6 py-3.5", className)}>{children}</div>;
}
